import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatMenu, MatMenuContent, MatMenuTrigger } from '@angular/material/menu';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { FilterableItemsListComponent, FilterableListItemDirective } from '@examdojo/core/filterable-items-list';
import { LocalizedStringComponent } from '@examdojo/core/i18n';
import { IconComponent } from '@examdojo/core/icon';
import { Unwrap } from '@examdojo/core/typescript';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { combineLatest, distinctUntilChanged, filter, map, merge, startWith, tap } from 'rxjs';
import { SyllabusStoreModel } from '../syllabus.model';
import { SyllabusQuery } from '../syllabus.query';
import { SyllabusService } from '../syllabus.service';

@Component({
  selector: 'exam-syllabus-select',
  standalone: true,
  imports: [
    FilterableItemsListComponent,
    FilterableListItemDirective,
    IconComponent,
    MatMenu,
    MatMenuContent,
    MatMenuTrigger,
    LocalizedStringComponent,
    ButtonComponent,
  ],
  templateUrl: './syllabus-select.component.html',
  styleUrl: './syllabus-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyllabusSelectComponent {
  constructor(
    private readonly syllabusQuery: SyllabusQuery,
    private readonly syllabusService: SyllabusService,
  ) {
    merge(this.setOptions(), this.setInitialValue(), this.setActiveSyllabusOnChange())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  readonly allowedSyllabuses = input<Array<SyllabusStoreModel['id']>>();

  readonly options = signal<SyllabusStoreModel[] | null>(null);

  readonly syllabusIdCtrl = new FormControl<SyllabusStoreModel['id'] | null>(null);

  readonly state = connectState({
    selectedOption: combineLatest([
      this.syllabusIdCtrl.valueChanges.pipe(startWith(this.syllabusIdCtrl.value)),
      toObservable(this.options),
    ]).pipe(map(([value, options]) => options?.find((option) => option.id === value))),
  });

  readonly filterPropertyValueFn = (item: NonNullable<Unwrap<typeof this.options>>[number]) => item.name;
  readonly identityFn = (item: NonNullable<Unwrap<typeof this.options>>[number]) => item.id.toString();

  setSelected(id: string) {
    this.syllabusIdCtrl.setValue(parseInt(id, 10));
  }

  private setOptions() {
    return combineLatest([this.syllabusQuery.entities$, toObservable(this.allowedSyllabuses)]).pipe(
      filter(([entities]) => !!entities.length),
      map(([entities, allowedSyllabuses]) =>
        allowedSyllabuses ? entities.filter((entity) => allowedSyllabuses.includes(entity.id)) : entities,
      ),
      tap((options) => this.options.set(options)),
    );
  }

  private setInitialValue() {
    return this.syllabusQuery.active$.pipe(
      filter(Boolean),
      tap((active) => this.syllabusIdCtrl.setValue(active.id)),
    );
  }

  private setActiveSyllabusOnChange() {
    return this.syllabusIdCtrl.valueChanges.pipe(
      distinctUntilChanged(),
      filter(isNotNullish),
      tap((id) => {
        this.syllabusService.setActive(id, null);
      }),
    );
  }
}
